import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {classes, vars} from './bymo-page-viewer.st.css';
import {BymoPageContextProps, BymoPageContextPropsContext} from '../../doppe-sdk';
import {
    BymoPageProps,
    BymoPagePropsContext,
    BymoPagePropsForViewer,
    bymoPagePropsResolveTextFontFamilyStyleSheetUrl,
    bymoPageResolveActionsFromPage,
    bymoTemplateDesktopLayoutEnsureSupportedLayout,
    DoppeBiEmptyTargetUrl
} from '../../client-server-common';
import {
    BymoPageViewerMode,
    bymoPageViewerModeIsPreview,
    useBymoPageViewerContext
} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {
    DoppeViewerPageInfo,
    doppeViewerPageInfoCreateHome
} from '../../client-server-common/viewer-pages/doppe-viewer-pages';
import {useDoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {
    ListViewIdsProvider,
    ModalsScope,
    reactCreateMemo,
    reactProvideContextValues,
    SsrSafeHtmlIdsProvider,
    useLayoutBinding,
    useObservableValue,
    useRootWindow,
    useWindowDependantMemoWithDefaultValue
} from '@wix/devzai-utils-react';
import {DomEventListener, domGetScrollbarSize, domLoadCSS} from '@wix/devzai-utils-dom';
import {arrayLast, immerProduce, objectGetEntries, referencesEqualityComparer} from '@wix/devzai-utils-common';
import {BymoPageStyleContext, bymoPageStyleResolveVars} from '../bymo-page-style/bymo-page-style';
import {BrowserHistory} from '@wix/devzai-common-client';
import {
    DoppeViewerColorPaletteCalculatedColorsDataReactContext,
    DoppeViewerHtmlSiteInfoReactContext,
    DoppeViewerLangReactContext,
    DoppeViewerPageDesktopLayoutReactContext,
    DoppeViewerShouldShowCookieBannerReactContext,
    DoppeViewerShouldShowPrivacyAndTermsReactContext
} from '../doppe-viewer-bymo-page-props-react/doppe-viewer-bymo-page-props-react';

const Features = {
    enableBackInterception: true
}

export const BymoPageViewer = reactCreateMemo(function BymoPageViewer(props: BymoPageViewer.Props) {
    const {
        bymoPageComponentType,
        bymoPagePropsForViewer,
        bymoPageContextProps,
        currentPageInfo: initialPageInfo
    } = props;

    const pageViewerContext = useBymoPageViewerContext();
    const clientServices = useDoppeViewerClientServices();

    // Sync with initialPageInfo prop changes (from editor panel navigation)
    // This effect ONLY runs in Preview/Editor modes, NOT in Live mode
    useEffect(() => {
        // This sync effect is ONLY for preview/editor modes
        // In Live mode, the router is the source of truth (not the prop)
        if (!bymoPageViewerModeIsPreview(pageViewerContext.mode)) {
            // Skip router navigation in Live mode - router controls navigation
            return;
        }


        // Use initialPageInfo directly (no conversion needed)
        const router = clientServices.viewerRouterClientService;
        const pageInfo = initialPageInfo ?? doppeViewerPageInfoCreateHome();
        const currentPageInfo = router.getCurrentPageInfo();

        // Only navigate if the page info has actually changed (prevents redundant navigation)
        if (!router.doppeViewerPageInfoEquals(currentPageInfo, pageInfo)) {
            router.navigateToPageInfo(pageInfo, { replace: true });
        }

    }, [initialPageInfo, pageViewerContext.mode, clientServices.viewerRouterClientService]);




    // Subscribe to router page changes and emit events to editor
    // In LivePreview mode: emit page navigation events to editor
    useEffect(() => {
        const reportPageView = () => {
            if (pageViewerContext.mode === BymoPageViewerMode.Live) {
                clientServices.viewerAnalyticsClient.logEvent('uouPageViewed', {
                    pageId: pageViewerContext.bymoPageId,
                    hypeUserId: pageViewerContext.doppeUserId,
                    linkId: pageViewerContext.linkId,
                    destinationUrl: bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                    pageType: pageViewerContext.pageType
                });

                clientServices.viewerBmAnalyticsDashboardBiClient.logEvent('pageView', {
                    url: window.location.href,
                    ref: document.referrer,
                    msid: clientServices.bymoService.getBymoLinkViewerData()?.creatorsMetaSiteId ?? '',
                    vid: clientServices.doppeViewerClientData.clientId,
                    vsi: clientServices.doppeViewerClientData.sessionId,
                    bsi: clientServices.doppeViewerClientData.sessionId,
                    viewer: 'unknown',
                });
            }
        }

        const router = clientServices.viewerRouterClientService;
        const unsubscribe = router.subscribe((pageInfo) => {
            // Emit event to editor in LivePreview mode (not in static Preview mode)
            if (pageViewerContext.mode === BymoPageViewerMode.LivePreview) {
                pageViewerContext.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventPageNavigationChanged', {
                    pageInfo: pageInfo
                });

            }

            if (!bymoPageViewerModeIsPreview(pageViewerContext.mode)) {
                reportPageView();
            }
        });

        reportPageView();

        return unsubscribe;
    }, [bymoPageContextProps.targetUrl, clientServices, pageViewerContext]);

    const rootWindow = useRootWindow();
    const modalsScopeRef = useRef<ModalsScope>(null);

    useLayoutEffect(() => {
        if (Features.enableBackInterception && pageViewerContext.mode !== BymoPageViewerMode.LivePreview) {
            const disposeBrowserHistoryInterception = BrowserHistory.interceptNavigation((_, action) => {

                if (action === 'POP') {
                    const topMostModal = arrayLast(modalsScopeRef.current?.getOpenModals() ?? []);

                    if (topMostModal) {
                        return BrowserHistory.interceptionAction(() => {
                            topMostModal.closeWindow();
                        });
                    }
                }

                return false;
            }, Infinity)

            return () => {
                disposeBrowserHistoryInterception();
            }
        } else {
            return undefined;
        }
    }, []);

    const actionsSettingsOverrides = useObservableValue(clientServices.bymoService.observableActionsSettingsOverrides);

    const bymoPageProps = useMemo(() => {
        // Get current page ID from router
        const router = clientServices.viewerRouterClientService;
        const currentPageInfo = router.getCurrentPageInfo();
        const currentPageId = currentPageInfo ? router. resolvePageId(currentPageInfo) : null;

        return immerProduce(bymoPagePropsForViewer, (bymoPagePropsForViewer) => {
            const actions = bymoPageResolveActionsFromPage(
                bymoPagePropsForViewer,
                currentPageId
            );

            objectGetEntries(actionsSettingsOverrides).forEach(([actionId, actionSettings]) => {
                const action = actions.find(action => action.id === actionId);
                if (action) {
                    action.settings = actionSettings;
                }
            });
        });
    }, [bymoPagePropsForViewer, actionsSettingsOverrides, clientServices]);

    const textFontFamilyStyleSheetUrl = useMemo(() => {
        return bymoPagePropsResolveTextFontFamilyStyleSheetUrl(bymoPageProps);
    }, [bymoPageProps]);

    useEffect(() => {
        if (pageViewerContext.mode !== BymoPageViewerMode.Live) {
            domLoadCSS(textFontFamilyStyleSheetUrl, {rootWindow: rootWindow})
                .catch(error => {
                    console.error(`Failed loading font css`, error)
                })
        }
    }, [textFontFamilyStyleSheetUrl])

    const hideBrowserScrollbar = bymoPageViewerModeIsPreview(pageViewerContext.mode);
    useEffect(() => {
        const currentWindow = (rootWindow ?? window);

        currentWindow.document.documentElement.classList.toggle(
            classes.hideBrowserScrollbar,
            hideBrowserScrollbar || (currentWindow.self !== currentWindow.top)
        );

    }, [hideBrowserScrollbar, rootWindow])

    const bymoPageNamespacedStyleVarsValues = useMemo(() => {
        return bymoPageStyleResolveVars({
            lang: bymoPageProps.lang,
            colorPalette: bymoPageProps.colorPalette,
            textFontFamily: bymoPageProps.textFontFamily,
            devCustomPageContentWidthInWideLayout: bymoPageProps.devCustomPageContentWidthInWideLayout
        })
    }, [
        bymoPageProps.lang,
        bymoPageProps.colorPalette,
        bymoPageProps.textFontFamily,
        bymoPageProps.devCustomPageContentWidthInWideLayout
    ]);

    const lastScrollTopRef = useRef<number>(0);

    const isIos = useWindowDependantMemoWithDefaultValue(
        window => /iPhone|iPad|iPod/.test(window.navigator.userAgent) || true,
        false,
        []
    );

    const [modalIsShown, setModalIsShown] = useState(false);

    const visualViewportHeight = useLayoutBinding(
        useCallback(() => (rootWindow ?? window).visualViewport?.height, [rootWindow]),
        referencesEqualityComparer,
        isIos && modalIsShown
    );

    const isModalShownOnIos = isIos && modalIsShown;

    useEffect(() => {
        if (isModalShownOnIos) {
            const targetWindow = rootWindow ?? window;

            let ignoreScrolling = false;
            const touchStartBinding = DomEventListener.bind(targetWindow.document, 'touchstart', () => {
                ignoreScrolling = true;
            })

            let scheduledScroll: any | undefined = undefined;
            const scheduleScroll = () => {
                clearTimeout(scheduledScroll);
                scheduledScroll = setTimeout(() => {
                    targetWindow.scrollTo({
                        top: 0
                    })
                }, 16)
            }


            const touchEndBinding = DomEventListener.bind(targetWindow.document, 'touchend', () => {
                ignoreScrolling = false;
                scheduleScroll();
            })

            const binding = DomEventListener.bind(targetWindow, 'scroll', () => {
                if (!ignoreScrolling) {
                    scheduleScroll();
                }
            })

            return () => {
                clearTimeout(scheduledScroll);
                binding.dispose();
                touchStartBinding.dispose();
                touchEndBinding.dispose();
            }
        } else {
            return undefined;
        }
    }, [isModalShownOnIos, rootWindow])

    useLayoutEffect(() => {
        (rootWindow ?? window).document.documentElement.style.setProperty(
            vars.visualViewportHeight,
            isIos && visualViewportHeight ? `${visualViewportHeight}px` : `100%`
        );
    }, [visualViewportHeight, isIos])

    useEffect(() => {
        return () => {
            const currentWindow = (rootWindow ?? window);
            const document = currentWindow.document;
            document.documentElement.classList.toggle(classes.modalShown, false);
            document.documentElement.classList.toggle(classes.modalShownWithClassicScrollbar, false);
        }
    }, [rootWindow]);

    return reactProvideContextValues()
        .addContextValue(BymoPageStyleContext, bymoPageNamespacedStyleVarsValues)
        .addContextValue(BymoPageContextPropsContext, bymoPageContextProps)
        .addContextValue(DoppeViewerHtmlSiteInfoReactContext, bymoPageContextProps.htmlSiteInfo)
        .addContextValue(BymoPagePropsContext, bymoPageProps)
        .addContextValue(DoppeViewerLangReactContext, bymoPageProps.lang)
        .addContextValue(DoppeViewerShouldShowCookieBannerReactContext, bymoPageProps.showCookieBanner)
        .addContextValue(DoppeViewerColorPaletteCalculatedColorsDataReactContext, bymoPageProps.colorPaletteCalculatedColorsData)
        .addContextValue(DoppeViewerShouldShowPrivacyAndTermsReactContext, bymoPageProps.showPrivacyAndTerms)
        .addContextValue(DoppeViewerPageDesktopLayoutReactContext,
            bymoTemplateDesktopLayoutEnsureSupportedLayout(
                bymoPageProps.pageLayout.layout,
                bymoPageProps.desktopLayout.layout
            )
        )
        .render(
            <ListViewIdsProvider>
                <SsrSafeHtmlIdsProvider>
                    <ModalsScope
                        ref={modalsScopeRef}
                        targetContainer={() => (rootWindow ?? window).document.body}
                        zIndex={999}
                        modalsClassName={classes.modalView}
                        onOpenModalsChange={(openModals) => {

                            const currentWindow = (rootWindow ?? window);
                            const document = currentWindow.document;
                            const siteMain = document.getElementById('SITE_MAIN');

                            if (openModals.length > 0) {
                                const lastScrollTop = lastScrollTopRef.current = document.documentElement.scrollTop;

                                const scrollbarSize = domGetScrollbarSize();
                                document.documentElement.style.setProperty(vars.scrollbarSize, `${domGetScrollbarSize()}px`);
                                document.documentElement.style.setProperty(vars.verticalShift, `-${lastScrollTop}px`);
                                document.documentElement.classList.toggle(classes.modalShown, true);
                                document.documentElement.classList.toggle(classes.modalShownWithClassicScrollbar, scrollbarSize > 0);

                                if (siteMain) {
                                    siteMain.scrollTop = lastScrollTop;
                                }

                                if (isIos) {
                                    const visualViewportHeight = currentWindow.visualViewport?.height
                                    document.documentElement.style.setProperty(
                                        vars.visualViewportHeight,
                                        isIos && visualViewportHeight ? `${visualViewportHeight}px` : `100%`
                                    )
                                }

                                setModalIsShown(true);
                            } else {

                                const restoreScrolling = () => {
                                    if (siteMain) {
                                        siteMain.scrollTop = 0;
                                    }

                                    document.documentElement.classList.toggle(classes.modalShown, false);
                                    document.documentElement.scrollTop = lastScrollTopRef.current;
                                    setModalIsShown(false);
                                }

                                if (Features.enableBackInterception) {
                                    setTimeout(() => {
                                        restoreScrolling();
                                    }, 100)
                                } else {
                                    restoreScrolling();
                                }
                            }

                            // document.documentElement.style.setProperty(vars.scrollbarSize, `${domGetScrollbarSize()}px`);
                            // document.documentElement.classList.toggle(classes.modalsShown, openModals.find(modalInfo => modalInfo.isFullScreen) !== undefined);
                        }}
                    >
                        {React.createElement(bymoPageComponentType, bymoPageProps)}
                    </ModalsScope>
                </SsrSafeHtmlIdsProvider>
            </ListViewIdsProvider>
        )
});

export namespace BymoPageViewer {

    export interface Props {
        bymoPageComponentType: React.ComponentType<BymoPageProps<any>>;
        bymoPagePropsForViewer: BymoPagePropsForViewer;
        bymoPageContextProps: BymoPageContextProps;
        currentPageInfo?: DoppeViewerPageInfo; // Optional parameter for multi-page support
    }
}